import { Pencil, Plus, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { HoldingGroupDto, InstrumentDto } from '../../shared/types';
import { api } from '../api';
import { holdingGroupSymbols, selectableHoldingGroupInstruments, sortHoldingGroupsByName } from '../holding-groups';

const markets = [{ key: 'TH_EQUITY', label: 'หุ้นไทย' }, { key: 'US_EQUITY', label: 'หุ้นสหรัฐ' }] as const;
type GroupForm = { id?: string; marketGroup: HoldingGroupDto['marketGroup']; name: string; instrumentIds: string[] };

export function HoldingGroupsSettings({ instruments, onSaved }: { instruments: InstrumentDto[]; onSaved: () => Promise<void> }) {
  const [groups, setGroups] = useState<HoldingGroupDto[]>([]);
  const [form, setForm] = useState<GroupForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function load() {
    setGroups(await api<HoldingGroupDto[]>('/holding-groups'));
  }
  useEffect(() => { load().catch((e) => setError(e.message)).finally(() => setLoading(false)); }, []);
  function edit(value: GroupForm) { setForm(value); setDeleting(null); setError(null); setNotice(null); }
  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!form) return;
    setBusy(true); setError(null);
    try {
      await api(form.id ? `/holding-groups/${form.id}` : '/holding-groups', {
        method: form.id ? 'PUT' : 'POST', body: JSON.stringify(form),
      });
      await Promise.all([load(), onSaved()]);
      setForm(null); setNotice('บันทึกกลุ่มย่อยแล้ว');
    } catch (e) { setError(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ'); }
    finally { setBusy(false); }
  }
  async function remove(id: string) {
    setBusy(true); setError(null);
    try {
      await api(`/holding-groups/${id}`, { method: 'DELETE' });
      await Promise.all([load(), onSaved()]);
      setDeleting(null); setNotice('ลบกลุ่มแล้ว หุ้นในกลุ่มย้ายไป “ยังไม่จัดกลุ่ม”');
    } catch (e) { setError(e instanceof Error ? e.message : 'ลบไม่สำเร็จ'); }
    finally { setBusy(false); }
  }
  const selectableInstruments = form
    ? selectableHoldingGroupInstruments(instruments, groups, form.marketGroup, form.id)
    : [];
  const sortedGroups = sortHoldingGroupsByName(groups);

  return <section className="panel subgroup-settings" id="holding-groups" aria-labelledby="subgroup-settings-title">
    <div className="panel-head"><div><span className="panel-kicker">CUSTOM GROUPS</span><h2 id="subgroup-settings-title">กลุ่มย่อยของหุ้น</h2></div></div>
    <p className="settings-copy">ตั้งชื่อกลุ่มและเลือกหุ้นได้เอง เช่น หุ้นเติบโต หรือหุ้นปันผล หุ้นแต่ละตัวอยู่ได้หนึ่งกลุ่มย่อย รวมทุกบัญชีที่ถือหุ้นตัวนั้น</p>
    {error && <p className="form-error" role="alert">{error}</p>}
    {notice && <p className="notice" role="status">{notice}</p>}
    {loading ? <p className="muted">กำลังโหลดกลุ่มย่อย…</p> : <div className="subgroup-settings-markets">
      {markets.map((market) => <div className="subgroup-market" key={market.key}>
        <div className="subgroup-market-head"><h3>{market.label}</h3><button className="button tertiary small" disabled={busy} onClick={() => edit({ marketGroup: market.key, name: '', instrumentIds: [] })}><Plus size={14} /> เพิ่มกลุ่ม{market.label}</button></div>
        {sortedGroups.filter((group) => group.marketGroup === market.key).map((group) => <div className="subgroup-setting-item" key={group.id}>
          <div className="subgroup-setting-row"><div><strong>{group.name}</strong><small>{holdingGroupSymbols(instruments, group.instrumentIds).join(' · ') || 'ยังไม่ได้เลือกหุ้น'}</small></div>
            <button className="icon-button" aria-label={`แก้ไขกลุ่ม ${group.name} ${market.label}`} disabled={busy} onClick={() => edit(group)}><Pencil size={15} /></button>
            <button className="icon-button danger-text" aria-label={`ลบกลุ่ม ${group.name} ${market.label}`} disabled={busy} onClick={() => { setDeleting(group.id); setError(null); }}><Trash2 size={15} /></button>
          </div>
          {deleting === group.id && <div className="subgroup-delete-confirm"><p>ลบกลุ่ม “{group.name}”? หุ้นจะย้ายไป “ยังไม่จัดกลุ่ม”</p><button className="button danger-text small" disabled={busy} onClick={() => remove(group.id)}>ยืนยันลบกลุ่ม</button><button className="button tertiary small" disabled={busy} onClick={() => setDeleting(null)}>ยกเลิก</button></div>}
        </div>)}
        {!sortedGroups.some((group) => group.marketGroup === market.key) && <p className="subgroup-settings-empty">ยังไม่มีกลุ่มย่อย</p>}
      </div>)}
    </div>}
    {form && <form className="subgroup-editor" onSubmit={save}>
      <div className="subgroup-market-head"><h3>{form.id ? 'แก้ไขกลุ่มย่อย' : 'เพิ่มกลุ่มย่อย'} · {markets.find((market) => market.key === form.marketGroup)?.label}</h3><button type="button" className="icon-button" aria-label="ปิดการแก้ไขกลุ่ม" disabled={busy} onClick={() => setForm(null)}><X size={17} /></button></div>
      <label className="field"><span>ชื่อกลุ่มย่อย</span><input required maxLength={60} value={form.name} disabled={busy} placeholder="เช่น หุ้นปันผล" onChange={(event) => setForm({ ...form, name: event.target.value })} /></label>
      <fieldset className="subgroup-stock-picker"><legend>เลือกหุ้นเข้ากลุ่ม</legend>
        {selectableInstruments.map((instrument) => <label className="subgroup-stock-option" key={instrument.id}>
            <input type="checkbox" checked={form.instrumentIds.includes(instrument.id)} disabled={busy} onChange={(event) => setForm({ ...form, instrumentIds: event.target.checked ? [...form.instrumentIds, instrument.id] : form.instrumentIds.filter((id) => id !== instrument.id) })} />
            <span><strong>{instrument.symbol}</strong><small>{instrument.name}</small></span>
          </label>)}
        {selectableInstruments.length === 0 && <p className="muted">ไม่มีหุ้นที่ยังไม่มีกลุ่มในตลาดนี้</p>}
      </fieldset>
      <div className="subgroup-editor-actions"><span>{form.instrumentIds.length} หุ้นที่เลือก</span><button type="button" className="button secondary small" disabled={busy} onClick={() => setForm(null)}>ยกเลิก</button><button className="button primary small" disabled={busy || !form.name.trim()}>{busy ? 'กำลังบันทึก…' : 'บันทึกกลุ่ม'}</button></div>
    </form>}
  </section>;
}
